import { getTotalMonthlyLeasingRate } from "./get-total-monthly-leasing-rate";
import type { OfferFormDataT } from "@/app/types/offers";

/**
 * Get the main price value from an offer for sorting/comparison purposes
 *
 * Priority order:
 * 1. Leasing: Base monthly leasing rate without maintenance/inspection costs
 * 2. Barkauf: Gross purchase price (anschaffungspreisBrutto)
 * 3. Finanzierung: Gross purchase price (anschaffungspreisBrutto), fallback to monthly rate (mtlRate)
 *
 * @param offer - The offer to extract price from
 * @returns The price value as a number, or null if no price is available
 *
 * @example
 * ```tsx
 * const price = getOfferPrice(offer);
 * if (price !== null) {
 *   // Use price for sorting or comparison
 * }
 * ```
 */
export function getOfferPrice(offer: OfferFormDataT): number | null {
  const pricing = offer.pricing;
  if (!pricing) return null;

  // Priority: leasing rate, then barkauf price, then finanzierung price
  if (pricing.type === "leasing") {
    const totalRate = getTotalMonthlyLeasingRate(offer);
    if (totalRate) return totalRate;
    if (pricing.leasing?.mtlLeasingrate) {
      return pricing.leasing.mtlLeasingrate;
    }
  }
  if (pricing.type === "barkauf" && pricing.barkauf?.anschaffungspreisBrutto) {
    return pricing.barkauf.anschaffungspreisBrutto;
  }
  if (
    pricing.type === "finanzierung" &&
    pricing.finanzierung?.anschaffungspreisBrutto
  ) {
    return pricing.finanzierung.anschaffungspreisBrutto;
  }
  if (pricing.type === "finanzierung" && pricing.finanzierung?.mtlRate) {
    return pricing.finanzierung.mtlRate;
  }
  return null;
}
